import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, BatteryCharging, Flame, Sun, Wind } from "lucide-react";

const FLOW_STEPS = ["Solar + Wind", "Energy Management", "Battery + Hydrogen System", "Farm Loads"];

const COMPONENTS = [
  {
    id: "solar-pv",
    title: "Solar PV",
    icon: Sun,
    description:
      "Converts daylight into on-site electricity for daytime farm loads and charging the storage layers.",
    desktopLines: [
      "Converts daylight into on-site electricity for daytime farm loads and charging",
      "the storage layers.",
    ],
    tags: ["On-site generation", "Daytime loads"],
  },
  {
    id: "wind",
    title: "Wind",
    icon: Wind,
    description:
      "Adds renewable generation during windy conditions and can complement solar production across different times of day and seasons.",
    tags: ["Renewable generation", "Complements solar"],
  },
  {
    id: "battery-buffer",
    title: "Battery Buffer",
    icon: BatteryCharging,
    description:
      "Provides fast, short-duration energy storage to respond to load fluctuations and stabilize critical farm equipment.",
    desktopLines: [
      "Provides fast, short-duration energy storage to respond to load fluctuations",
      "and stabilize critical farm equipment.",
    ],
    tags: ["Fast response", "Short-duration storage"],
  },
  {
    id: "hydrogen-storage",
    title: "Hydrogen System",
    icon: Flame,
    description:
      "Converts surplus renewable electricity into hydrogen for long-duration energy storage and backup during extended periods of low generation or grid outages.",
    tags: ["Long-Duration Reserve", "Extended Outages"],
  },
];

const ArchitectureSelector = ({ component, active, onSelect }) => {
  const Icon = component.icon;

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-controls="architecture-component-panel"
      data-testid={`architecture-selector-${component.id}`}
      onClick={() => onSelect(component.id)}
      className={`group flex min-h-[64px] w-full items-center gap-3.5 rounded-2xl border px-5 py-3 text-left transition-[background-color,border-color,box-shadow,transform] duration-300 ${active ? "border-teal/30 bg-white shadow-[0_12px_30px_-24px_rgba(30,83,92,0.8)] ring-1 ring-teal/10" : "border-borderLight bg-baseLight/70 hover:-translate-y-0.5 hover:border-borderSubtle hover:bg-white"}`}
    >
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${active ? "border-teal/25 bg-glacier text-teal" : "border-borderLight bg-white text-inkMuted group-hover:text-teal"}`}>
        <Icon size={19} strokeWidth={1.7} />
      </span>
      <span className={`font-heading text-base font-semibold transition-colors duration-300 ${active ? "text-teal" : "text-ink"}`}>
        {component.title}
      </span>
    </button>
  );
};

export const SystemArchitecture = () => {
  const [activeId, setActiveId] = useState(COMPONENTS[0].id);
  const activeComponent = COMPONENTS.find((component) => component.id === activeId);
  const ActiveIcon = activeComponent.icon;

  return (
    <section id="architecture" data-testid="system-architecture-section" className="bg-surfaceLight px-6 pt-[15px] pb-[30px] sm:py-[40px] lg:px-12 lg:pt-[50px] lg:pb-[70px]">
      <div className="lg:mx-auto lg:max-w-[1280px]">
        <h2 data-testid="system-architecture-title" className="font-heading text-[30px] font-semibold leading-tight text-ink sm:text-[35px] lg:text-center lg:text-[41px]">
          How the hybrid energy system works
        </h2>
        <p data-testid="system-architecture-description" className="mt-4 max-w-2xl text-[15px] leading-relaxed text-inkSoft lg:mx-auto lg:text-center">
          Renewable generation powers farm loads first. An energy management system coordinates
          battery response and long-duration hydrogen storage based on available generation and
          operational demand.
        </p>

        <div data-testid="architecture-flow-bar" className="mt-6 flex flex-wrap items-center gap-2 text-xs font-medium text-inkMuted sm:text-sm lg:justify-center lg:mb-[55px]">
          {FLOW_STEPS.map((step, index) => (
            <span key={step} className="flex items-center gap-2">
              <span className="rounded-full border border-borderLight bg-white px-3 py-1.5 text-ink">{step}</span>
              {index !== FLOW_STEPS.length - 1 && <ArrowRight size={13} className="text-teal shrink-0" />}
            </span>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-10">
          <div role="tablist" aria-label="System architecture components" className="flex flex-col gap-3 lg:col-span-5">
            {COMPONENTS.map((component) => (
              <ArchitectureSelector
                key={component.id}
                component={component}
                active={component.id === activeId}
                onSelect={setActiveId}
              />
            ))}
          </div>

          <div
            id="architecture-component-panel"
            role="tabpanel"
            data-testid="architecture-info-card"
            className="min-h-[270px] overflow-hidden rounded-3xl border border-borderLight bg-white p-6 sm:p-7 lg:col-span-7 lg:p-8"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeComponent.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="h-full"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-teal/20 bg-glacier text-teal">
                    <ActiveIcon size={23} strokeWidth={1.7} />
                </div>
                <h3 data-testid="architecture-card-title" className="mt-6 max-w-xl font-heading text-2xl font-semibold leading-tight text-ink sm:text-3xl">
                  {activeComponent.title}
                </h3>
                <p data-testid="architecture-card-description" className="mt-4 max-w-2xl text-[15px] leading-relaxed text-inkSoft">
                  {activeComponent.desktopLines ? (
                    <>
                      {activeComponent.desktopLines[0]}{" "}
                      <br className="hidden min-[992px]:block" />
                      {activeComponent.desktopLines[1]}
                    </>
                  ) : (
                    activeComponent.description
                  )}
                </p>
                <div data-testid="architecture-card-tags" className="mt-6 flex flex-wrap gap-2">
                  {activeComponent.tags.map((tag, index) => (
                    <span
                      key={tag}
                      data-testid={`architecture-card-tag-${index}`}
                      className="rounded-full border border-teal/15 bg-glacier px-3 py-1.5 text-xs font-medium text-teal"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};